import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, CreditCard, Loader2, Sparkles, ShieldCheck } from "lucide-react";

const PLANS = [
  { name: "Starter", key: "starter", monthly: 99, yearly: 990, tag: "Entry-level",
    features: ["1 WhatsApp Number", "2 Team Members", "Shared Team Inbox", "Basic CRM & Tags", "Basic Automation"] },
  { name: "Growth", key: "growth", monthly: 299, yearly: 2990, popular: true, tag: "Most Popular",
    features: ["5 Team Members", "AI Chatbot + Knowledge Base", "Lead Qualification", "Approved Template Manager", "Broadcast + Segmentation", "Sales Pipeline"] },
  { name: "Pro", key: "pro", monthly: 599, yearly: 5990, tag: "Best for scaling",
    features: ["Up to 2 WhatsApp Numbers", "15 Team Members", "AI Sales Agent + Lead Scoring", "Smart Routing + AI Summary", "Campaign Analytics", "Revenue Tracking"] },
  { name: "Business", key: "business", monthly: 999, yearly: 9990, tag: "Multi-branch",
    features: ["Up to 5 WhatsApp Numbers", "30 Team Members", "Multi-Branch + Supervisor", "Advanced Automation + SLA", "Management AI", "Higher API Limits"] },
];

export default function Billing() {
  const { user } = useAuth();
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const { data: ws } = useQuery({ queryKey: ["workspace-plan"], queryFn: () => api.get("/auth/me").then((r) => r.data) });
  const currentPlan = ws?.workspace_plan;

  const subscribe = async (planKey) => {
    setLoading(planKey);
    try {
      const lookup_key = `${planKey}_${cycle}`;
      const res = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Gagal memulakan pembayaran");
      setLoading(null);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="billing-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><CreditCard className="h-6 w-6 text-primary" /> Langganan & Pembayaran</h1>
        <p className="text-muted-foreground text-sm mt-1">Naik taraf pelan anda. Pembayaran selamat melalui Stripe.</p>
      </div>

      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm font-medium ${cycle === "monthly" ? "text-foreground" : "text-muted-foreground"}`}>Bulanan</span>
        <button data-testid="billing-cycle-toggle" onClick={() => setCycle((c) => (c === "monthly" ? "yearly" : "monthly"))}
          className="relative h-7 w-12 rounded-full bg-primary/20 transition-colors">
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-primary transition-all ${cycle === "yearly" ? "left-6" : "left-1"}`} />
        </button>
        <span className={`text-sm font-medium ${cycle === "yearly" ? "text-foreground" : "text-muted-foreground"}`}>
          Tahunan <Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-full ml-1">2 bulan percuma</Badge>
        </span>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PLANS.map((p) => {
          const isCurrent = currentPlan === p.name;
          return (
            <div key={p.key} data-testid={`billing-plan-${p.key}`}
              className={`relative rounded-2xl border bg-card p-6 flex flex-col ${p.popular ? "border-primary ring-2 ring-primary shadow-xl" : "border-border"}`}>
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{p.tag}</p>
              <h3 className="font-display text-xl font-bold mt-1">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-bold font-display">RM{cycle === "monthly" ? p.monthly : p.yearly}</span>
                <span className="text-muted-foreground text-sm">/{cycle === "monthly" ? "bulan" : "tahun"}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => subscribe(p.key)} disabled={loading === p.key || isCurrent}
                data-testid={`billing-subscribe-${p.key}`}
                className={`mt-6 rounded-full w-full ${p.popular ? "bg-primary hover:bg-primary/90 text-primary-foreground" : ""}`}
                variant={p.popular ? "default" : "outline"}>
                {isCurrent ? "Pelan Semasa" : loading === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Langgan Sekarang"}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="max-w-3xl mx-auto rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-4 text-sm text-amber-800 dark:text-amber-300 flex gap-3">
        <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" />
        <p>Langganan platform TIDAK termasuk caj mesej WhatsApp/Meta & penggunaan AI melebihi kuota — dikira berasingan untuk melindungi margin. Enterprise (dari RM1,999) — hubungi jualan.</p>
      </div>

      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" /> Mod ujian Stripe — guna kad <span className="font-mono">4242 4242 4242 4242</span>, sebarang tarikh & CVC.
      </p>
    </div>
  );
}
